import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { ProductivitySortFields, SortDirection, GetProductivitiesQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/productivity';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useProductivitiesTable = ({ queryOptions }: { queryOptions?: GetProductivitiesQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetProductivitiesQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as ProductivitySortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withUser: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: productivityData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetProductivitiesQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      productivityData
        ? { totalCount: productivityData.productivities.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [productivityData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type ProductivitiesBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function ProductivitiesBaseTable(props: ProductivitiesBaseTableProps) {
  const tableProps = useProductivitiesTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
